import {pageDimensions} from '@pb/kit-util-page-dimensions';

/*
* Use this className to mark elements that should be taken into account when calculating the scroll offset.
* These are elements like page sticky headers, that are always visible (fixed, sticky) and should be taken into account.
* Sticky section headers cannot be marked with this class, because they are not always visible.
* See getScrollOffset() function.
* */
export const impactScrollSpyOffsetClassName = 'impact-scroll-spy-offset';

type ScrollBehavior = 'auto' | 'smooth' | 'instant';
interface ScrollOptions {
    behavior?: ScrollBehavior,
}
type ScrollLogicalPosition = 'center' | 'end' | 'nearest' | 'start';
interface ScrollIntoViewOptions extends ScrollOptions {
    block?: ScrollLogicalPosition,
    inline?: ScrollLogicalPosition,

    /**
     * @deprecated It will be removed in 6 months 09-15-2025. Use CSS property `scroll-margin-top` on the target node instead.
     * */
    offset?: number | (() => number),
}
export type Options = boolean | ScrollIntoViewOptions;

export type ScrollToDomNodeConfigType = {
    node: (Element | null | undefined) | (Text | null | undefined),
    options?: Options,
};

const getElementsHeight = (sum: number, selector: HTMLElement | string) => {
    const collection =
        selector instanceof HTMLElement
            ? [selector]
            : document.querySelectorAll(selector);
    return Array.from(collection)
        // @ts-expect-error
        .map(el => el.offsetHeight || 0)
        .reduce((acc, height) => acc + height, sum);
};

/*
* Calculate the height of all elements with the impactScrollSpyOffsetClassName.
* */
export const getScrollOffset = (): number => {
    const selectors = [`.${impactScrollSpyOffsetClassName}`].filter(Boolean);

    const offset = selectors.reduce(getElementsHeight, 0);

    return pageDimensions.getHeaderHeight() + (offset > 0 ? offset - 1 : offset);
};

/**
 * @deprecated It will be removed in 6 months 09-15-2025. Use document.body.scrollIntoView() instead
 * */
export const scrollTop = () => {
    window.scrollTo({top: 0});
};

/**
 * Utility function to scroll to a specific DOM node.
 *
 * @param node - The DOM node to scroll to.
 * @param options - The options to pass to the `scrollIntoView` method.
 * @param options.offset - The offset from the top of the viewport to scroll to. By default, it uses the `getScrollOffset` function.
 *
 * Features:
 *
 * - It uses the `scrollIntoView` method to scroll to the DOM node.
 * - It uses `getScrollOffset` to calculate the offset to scroll to.
 * - You can change offset in 2 ways:
 *      by adding CSS property `scroll-margin-top` to the element.
 *      by passing `options.offset` to the function.
 * - Returns a promise that resolves when the scroll is complete.
 */
export const scrollToDomNode = ({
    node,
    options,
}: ScrollToDomNodeConfigType) => new Promise(resolve => {
    if (node && node instanceof HTMLElement) {
        const hasOwnScrollMargin = window.getComputedStyle(node).scrollMarginTop !== '0px';
        if (!hasOwnScrollMargin) {
            let offset;
            if (options && typeof options === 'object' && options.offset) {
                offset = typeof options.offset === 'function' ? options.offset() : options.offset;
            } else {
                offset = getScrollOffset();
            }
            node.style.scrollMarginTop = `${offset}px`;
        }
        requestAnimationFrame(() => {
            let scrollTimeout: ReturnType<typeof setTimeout>;
            const listener = () => {
                clearTimeout(scrollTimeout);
                scrollTimeout = setTimeout(() => {
                    if (!hasOwnScrollMargin) {
                        node.style.scrollMarginTop = '';
                    }
                    removeEventListener('scroll', listener);
                    resolve(undefined);
                }, 100);
            };
            addEventListener('scroll', listener, {passive: true});
            setTimeout(listener, 100);
            node.scrollIntoView(options);
        });
    } else {
        resolve(undefined);
    }
});

/**
 * Function to scroll to a specific DOM node by CSS selector.
 * */
export const scrollToDOMNodeBySelector = (selector: string, options?: Options) => {
    selector = selector.replace(/([/*])/g, '\\$1');
    return scrollToDomNode({node: document.querySelector(selector), options});
};
